import { useEffect, useId, useState } from "react";
import type { Limit } from "../model/limits.ts";
import { clamp } from "../model/limits.ts";
import { amountReadout, groupAmount, parseAmount } from "../lib/format.ts";
import type { AmountUnit } from "../lib/format.ts";

interface Props {
  label: string;
  value: number;
  limit: Limit;
  onCommit: (next: number) => void;
  /** Convert the stored value into what the user types, and back. */
  toDisplay?: (v: number) => number;
  fromDisplay?: (v: number) => number;
  hint?: string;
  suffix?: string;
  indent?: boolean;
  /** Money field: shows digit grouping, accepts shorthand (4.5L, 1.2cr, 250k, 1.2M) and echoes the amount. */
  unit?: AmountUnit;
}

/**
 * A number input that does not fight the person typing.
 *
 * Clamping on every keystroke makes a field with a minimum of 30 impossible to
 * type into — "4" on the way to "45" snaps to 30 and eats the next character.
 * So the draft is held locally while the field has focus and only committed,
 * clamped, on blur or Enter. Out-of-range entries are corrected rather than
 * rejected, and the field says so.
 */
export function NumberField({
  label,
  value,
  limit,
  onCommit,
  toDisplay = (v) => v,
  fromDisplay = (v) => v,
  hint,
  suffix,
  indent,
  unit,
}: Props) {
  const id = useId();
  const [draft, setDraft] = useState<string | null>(null);
  const [corrected, setCorrected] = useState(false);

  // If the value changes from outside (a preset, a shared link), drop the draft.
  useEffect(() => setDraft(null), [value]);

  const shown = draft ?? (unit ? groupAmount(toDisplay(value), unit) : String(toDisplay(value)));
  const draftValue = draft === null ? value : fromDisplay(parseAmount(draft, unit));

  const commit = () => {
    if (draft === null) return;
    if (draft.trim() === "") {
      setDraft(null);
      setCorrected(true);
      return;
    }
    const typed = fromDisplay(parseAmount(draft, unit));
    const next = clamp(typed, limit, value);
    setCorrected(Math.abs(next - typed) > 1e-9 || !Number.isFinite(typed));
    setDraft(null);
    if (next !== value) onCommit(next);
  };

  const displayLimit = {
    min: toDisplay(limit.min),
    max: toDisplay(limit.max),
    step: toDisplay(limit.step) - toDisplay(0) || limit.step,
  };

  return (
    <div className={indent ? "field indent" : "field"}>
      <label htmlFor={id}>{label}</label>
      <div className="field-row">
        <input
          id={id}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          spellCheck={false}
          data-min={displayLimit.min}
          data-max={displayLimit.max}
          value={shown}
          aria-describedby={`${id}-hint`}
          onChange={(e) => {
            setDraft(e.target.value);
            setCorrected(false);
          }}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            }
          }}
        />
        {suffix && <span className="suffix">{suffix}</span>}
        {unit && (
          <span className="echo" aria-live="polite">
            {Number.isFinite(draftValue) ? `= ${amountReadout(draftValue, unit)}` : "?"}
          </span>
        )}
      </div>
      <small id={`${id}-hint`} className={corrected ? "hint corrected" : "hint"}>
        {corrected
          ? `Adjusted to fit ${limit.hint}.`
          : draft !== null && !Number.isFinite(draftValue)
            ? unit === "inr"
              ? "Type a number, or shorthand like 4.5L or 1.2cr."
              : unit === "usd"
                ? "Type a number, or shorthand like 250k or 1.2M."
                : "Type a number."
            : (hint ?? `Allowed: ${limit.hint}`)}
      </small>
    </div>
  );
}
